import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";

export const RXDB_NOTIFICATION_DISMISSAL_COLLECTION_NAME =
  "notification-dismissal";

export const useRxCollectionNotificationDismissal =
  (): RxCollection<NotificationDismissal> | null =>
    useRxCollection<NotificationDismissal>(
      RXDB_NOTIFICATION_DISMISSAL_COLLECTION_NAME,
    );

const NOTIFICATION_DISMISSAL_SCHEMA_LITERAL = {
  title: "notification dismissal schema",
  version: 2,
  description:
    "Per-user record of a dismissed notification, optionally bound to a producer-supplied version",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      // Length-prefix encoded: see `deterministic-ids.ts → dismissalId`.
      // `dismissal-` (10) + "100-" (4) + userId (100) + "-" + "200-" (4) +
      // key (200) + "-" + "1-100-" (6) + version (100) = 426.
      type: "string",
      maxLength: 440,
    },
    userId: {
      type: "string",
      maxLength: 100,
    },
    key: {
      type: "string",
      maxLength: 200,
    },
    versionAtDismissal: {
      // Bounded so the composite id can't exceed its maxLength. Producers that
      // care about dismissal-survival semantics are expected to use short tags.
      type: ["string", "null"],
      maxLength: 100,
    },
    dismissedAt: {
      type: "string",
      format: "date-time",
    },
    meta: SYNCABLE_META,
  },
  required: ["id", "userId", "key", "dismissedAt", "meta"],
  indexes: ["userId", "key"],
  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(NOTIFICATION_DISMISSAL_SCHEMA_LITERAL);

export type NotificationDismissal = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export const NOTIFICATION_DISMISSAL_SCHEMA: RxJsonSchema<NotificationDismissal> =
  NOTIFICATION_DISMISSAL_SCHEMA_LITERAL;
